import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  AlertOctagon,
  CheckCircle2,
  Clock4,
  Download,
  Lock,
  Plus,
  Search,
  ShieldAlert,
} from 'lucide-react'
import { Card, CardContent } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Skeleton } from '@/components/ui/Skeleton'
import { useBlocks } from './hooks/useBlocks'
import {
  useApproveBlock,
  useLiftBlock,
  useRejectBlock,
} from './hooks/useBlockMutations'
import { useDebounce } from '@/hooks/useDebounce'
import { useCan } from '@/lib/permissions'
import { formatTimeAgo } from '@/lib/format'
import type {
  BlockSource,
  BlockStatus,
  BlockSummary,
  BlockType,
} from './types'

const TYPES: BlockType[] = [
  'account',
  'customer',
  'card',
  'bvn',
  'device',
  'ip',
  'terminal',
]
const STATUSES: BlockStatus[] = [
  'requested',
  'approved',
  'active',
  'lifted',
  'rejected',
  'expired',
]
const SOURCES: BlockSource[] = [
  'alert',
  'case',
  'manual',
  'automated',
  'regulatory_order',
  'court',
  'efcc',
  'nfiu',
]

/**
 * Active Blocks — catalogue-aligned rebuild of /blocks.
 *
 *   - KPI strip: Active / Awaiting approval / Approved (24h) /
 *                Rejected (7d) / Regulatory holds.
 *   - Period chip group (catalogue style): All / 24h / 7d / 30d.
 *   - Search across number / target / reason / source ref.
 *   - Filter chips: Status / Type / Source.
 *   - Rich table: Block #, Target+Type, Source, Reason, Effective
 *                 period, Status, inline Actions (Approve / Reject /
 *                 Lift — permission-gated).
 *
 * Backed by the existing GET /blocks endpoint — every column the
 * page renders comes out of the live wire shape.
 */
export function ActiveBlocksPage() {
  const [search, setSearch] = useState('')
  const [type, setType] = useState<BlockType[]>([])
  const [status, setStatus] = useState<BlockStatus[]>([])
  const [source, setSource] = useState<BlockSource[]>([])
  const [period, setPeriod] = useState<'all' | 'h24' | 'd7' | 'd30'>('all')
  const debounced = useDebounce(search, 250)

  const args = useMemo(
    () => ({ filters: { type, status, q: debounced || undefined } }),
    [type, status, debounced],
  )
  const { data, isLoading } = useBlocks(args)
  const blocks = useMemo(() => data?.blocks ?? [], [data?.blocks])

  // Period + source filtering live client-side — the backend doesn't
  // expose those filters yet and the dataset is bounded enough that
  // filtering in memory is fine.
  const filtered = useMemo(() => {
    return blocks.filter((b) => {
      if (source.length > 0 && !source.includes(b.source)) return false
      if (period !== 'all') {
        const cutoff = periodCutoff(period)
        const ts = b.requested_at ?? b.created_at ?? b.effective_from
        if (Date.parse(ts) < cutoff) return false
      }
      return true
    })
  }, [blocks, source, period])

  const kpis = useMemo(() => deriveKpis(blocks), [blocks])

  function exportCsv() {
    const header = [
      'block_number',
      'type',
      'status',
      'source',
      'target',
      'effective_from',
      'effective_to',
      'reason',
    ].join(',')
    const lines = filtered.map((b) =>
      [
        csv(b.block_number),
        b.block_type ?? b.type ?? '',
        b.status,
        b.source,
        csv(targetLabel(b)),
        b.effective_from ?? '',
        b.effective_to ?? '',
        csv(b.reason ?? ''),
      ].join(','),
    )
    const blob = new Blob([header + '\n' + lines.join('\n')], {
      type: 'text/csv',
    })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `blocks_${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="flex flex-col gap-4 p-6">
      {/* ---- Header ----------------------------------------------- */}
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-primary">
            Compliance · Account Holds
          </p>
          <h1 className="text-2xl font-bold tracking-tight">Block Register</h1>
          <p className="text-sm text-muted-fg">
            Account, BVN, card, device, IP and terminal holds across the platform —
            with maker-checker workflow + regulatory-order audit trail.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <PeriodChip value={period} onChange={setPeriod} />
          <Button variant="outline" size="sm" onClick={exportCsv}>
            <Download size={12} className="mr-1" />
            Export CSV
          </Button>
          <Button asChild variant="primary" size="sm">
            <Link to="/blocks/new">
              <Plus size={14} className="mr-1" />
              Request block
            </Link>
          </Button>
        </div>
      </header>

      {/* ---- KPI strip ------------------------------------------- */}
      <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <Kpi
          icon={<Lock className="h-4 w-4 text-primary" />}
          label="Active blocks"
          value={kpis.active}
          loading={isLoading}
        />
        <Kpi
          icon={<Clock4 className="h-4 w-4 text-status-pending" />}
          label="Awaiting approval"
          value={kpis.requested}
          tone={kpis.requested > 0 ? 'warn' : 'ok'}
          loading={isLoading}
        />
        <Kpi
          icon={<CheckCircle2 className="h-4 w-4 text-status-success" />}
          label="Approved (24h)"
          value={kpis.approvedRecent}
          loading={isLoading}
        />
        <Kpi
          icon={<AlertOctagon className="h-4 w-4 text-status-failed" />}
          label="Rejected (7d)"
          value={kpis.rejectedRecent}
          loading={isLoading}
        />
        <Kpi
          icon={<ShieldAlert className="h-4 w-4 text-priority-critical" />}
          label="Regulatory holds"
          value={kpis.regulatory}
          loading={isLoading}
        />
      </section>

      {/* ---- Toolbar / filters ----------------------------------- */}
      <Card>
        <CardContent className="flex flex-col gap-3 py-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search
                size={12}
                className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-muted-fg"
                aria-hidden
              />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by number, target, reason…"
                aria-label="Search blocks"
                className="h-8 w-72 pl-7 text-xs"
              />
            </div>
            <span className="ml-auto text-xs text-muted-fg">
              {filtered.length} of {blocks.length} block
              {blocks.length === 1 ? '' : 's'}
            </span>
          </div>
          <FilterRow
            label="Status"
            options={STATUSES.map((s) => ({ value: s, label: s }))}
            active={status}
            onToggle={(v) => setStatus(toggle(status, v as BlockStatus))}
          />
          <FilterRow
            label="Type"
            options={TYPES.map((t) => ({ value: t, label: t }))}
            active={type}
            onToggle={(v) => setType(toggle(type, v as BlockType))}
          />
          <FilterRow
            label="Source"
            options={SOURCES.map((s) => ({
              value: s,
              label: s.replace(/_/g, ' '),
            }))}
            active={source}
            onToggle={(v) => setSource(toggle(source, v as BlockSource))}
          />
        </CardContent>
      </Card>

      {/* ---- Table ----------------------------------------------- */}
      <Card>
        <CardContent className="p-0">
          {isLoading && filtered.length === 0 ? (
            <div className="space-y-2 p-4">
              <Skeleton />
              <Skeleton />
              <Skeleton />
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center text-sm text-muted-fg">
              No blocks match the current filters.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="border-b border-border bg-muted/40 text-[0.625rem] uppercase tracking-wider text-muted-fg">
                  <tr>
                    <th scope="col" className="px-3 py-2 text-left">Block #</th>
                    <th scope="col" className="px-3 py-2 text-left">Target</th>
                    <th scope="col" className="px-3 py-2 text-left">Source</th>
                    <th scope="col" className="px-3 py-2 text-left">Reason</th>
                    <th scope="col" className="px-3 py-2 text-left">Effective</th>
                    <th scope="col" className="px-3 py-2 text-left">Status</th>
                    <th scope="col" className="px-3 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((b) => (
                    <BlockRow key={b.id} block={b} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

// ---- BlockRow -------------------------------------------------------

function BlockRow({ block }: { block: BlockSummary }) {
  const approve = useApproveBlock()
  const reject = useRejectBlock()
  const lift = useLiftBlock()
  const canApprove = useCan('blocks:approve')
  const canLift = useCan('blocks:lift')
  const blockType = (block.block_type ?? block.type ?? 'customer')
  const isRequested = block.status === 'requested' || block.status === 'pending'
  const isActive = block.status === 'active'
  const requesterDisplay =
    block.requested_by?.display_name ??
    (block.requested_by_id ? `user #${block.requested_by_id}` : '—')

  function onApprove(e: React.MouseEvent) {
    e.stopPropagation()
    approve.mutate({ block_id: block.id })
  }
  function onReject(e: React.MouseEvent) {
    e.stopPropagation()
    const reason = window.prompt('Rejection reason (≥10 chars)?')
    if (reason && reason.trim().length >= 10) {
      reject.mutate({ block_id: block.id, reason: reason.trim() })
    }
  }
  function onLift(e: React.MouseEvent) {
    e.stopPropagation()
    const reason = window.prompt('Lift reason (≥10 chars)?')
    if (reason && reason.trim().length >= 10) {
      lift.mutate({ block_id: block.id, reason: reason.trim() })
    }
  }

  return (
    <tr className="border-b border-border align-top last:border-b-0 hover:bg-muted/40">
      <td className="px-3 py-2 align-top">
        <Link
          to={`/blocks/${block.id}`}
          className="font-mono text-[0.6875rem] tabular-nums text-primary hover:underline"
        >
          {block.block_number}
        </Link>
        <div className="mt-0.5 text-[0.625rem] text-muted-fg">
          {formatTimeAgo(
            block.requested_at ?? block.created_at ?? block.effective_from,
            '—',
          )}
        </div>
      </td>
      <td className="px-3 py-2 align-top">
        <div className="flex items-center gap-1.5">
          <TypeBadge type={blockType} />
          <span className="font-medium">{targetLabel(block)}</span>
        </div>
        <div className="mt-0.5 text-[0.625rem] text-muted-fg">
          Requested by {requesterDisplay}
        </div>
      </td>
      <td className="px-3 py-2 align-top">
        <div className="flex items-center gap-1.5">
          <SourceBadge source={block.source} />
          {block.branch_code ? (
            <span
              className="rounded bg-primary/10 px-1.5 py-0.5 font-mono text-[0.625rem] text-primary"
              title={block.branch_name ?? block.branch_code}
            >
              {block.branch_code}
            </span>
          ) : null}
        </div>
        {block.source_reference ? (
          <div
            className="mt-0.5 truncate font-mono text-[0.625rem] text-muted-fg"
            title={block.source_reference}
          >
            {block.source_reference}
          </div>
        ) : null}
      </td>
      <td className="px-3 py-2 align-top">
        <span
          className="line-clamp-2 max-w-md text-muted-fg"
          title={block.reason ?? ''}
        >
          {block.reason ?? '—'}
        </span>
      </td>
      <td className="px-3 py-2 align-top">
        <div className="text-[0.6875rem]">
          From <span className="font-mono">{formatDay(block.effective_from)}</span>
        </div>
        <div className="text-[0.625rem] text-muted-fg">
          {block.effective_to
            ? `until ${formatDay(block.effective_to)}`
            : isActive
              ? 'open-ended'
              : '—'}
        </div>
      </td>
      <td className="px-3 py-2 align-top">
        <BlockStatusBadge status={block.status} />
      </td>
      <td className="px-3 py-2 align-top text-right">
        <div className="inline-flex flex-wrap items-center justify-end gap-1">
          {isRequested && canApprove ? (
            <>
              <Button
                type="button"
                variant="primary"
                size="sm"
                disabled={approve.isPending}
                onClick={onApprove}
              >
                Approve
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={reject.isPending}
                onClick={onReject}
              >
                Reject
              </Button>
            </>
          ) : null}
          {isActive && canLift ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={lift.isPending}
              onClick={onLift}
            >
              Lift
            </Button>
          ) : null}
          <Button asChild type="button" variant="ghost" size="sm">
            <Link to={`/blocks/${block.id}`}>Open</Link>
          </Button>
        </div>
      </td>
    </tr>
  )
}

// ---- Subcomponents -------------------------------------------------

function Kpi({
  icon,
  label,
  value,
  loading,
  tone = 'ok',
}: {
  icon: React.ReactNode
  label: string
  value: number
  loading?: boolean
  tone?: 'ok' | 'warn'
}) {
  return (
    <Card className={tone === 'warn' ? 'border-amber-300/60' : undefined}>
      <CardContent className="space-y-1 py-3">
        <div className="flex items-center justify-between text-[0.625rem] uppercase tracking-wider text-muted-fg">
          <span>{label}</span>
          {icon}
        </div>
        {loading ? (
          <Skeleton />
        ) : (
          <p className="text-xl font-semibold tabular-nums">{value}</p>
        )}
      </CardContent>
    </Card>
  )
}

function FilterRow({
  label,
  options,
  active,
  onToggle,
}: {
  label: string
  options: Array<{ value: string; label: string }>
  active: string[]
  onToggle: (v: string) => void
}) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="text-[0.6875rem] uppercase tracking-wider text-muted-fg">
        {label}:
      </span>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onToggle(o.value)}
          className={
            'rounded border px-2 py-0.5 text-[0.6875rem] capitalize ' +
            (active.includes(o.value)
              ? 'border-primary bg-primary text-primary-fg'
              : 'border-border bg-card text-muted-fg hover:border-primary')
          }
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

function PeriodChip({
  value,
  onChange,
}: {
  value: 'all' | 'h24' | 'd7' | 'd30'
  onChange: (v: 'all' | 'h24' | 'd7' | 'd30') => void
}) {
  const PRESETS: Array<['all' | 'h24' | 'd7' | 'd30', string]> = [
    ['all', 'All'],
    ['h24', '24h'],
    ['d7', '7d'],
    ['d30', '30d'],
  ]
  return (
    <div className="flex items-center gap-0.5 rounded border border-border bg-card p-0.5 text-[0.6875rem]">
      {PRESETS.map(([k, label]) => (
        <button
          key={k}
          type="button"
          onClick={() => onChange(k)}
          className={
            'rounded px-1.5 py-0.5 ' +
            (value === k
              ? 'bg-primary text-primary-fg'
              : 'text-muted-fg hover:bg-muted hover:text-fg')
          }
        >
          {label}
        </button>
      ))}
    </div>
  )
}

function TypeBadge({ type }: { type: BlockType }) {
  const map: Record<BlockType, React.ComponentProps<typeof Badge>['variant']> = {
    customer: 'info',
    account: 'info',
    bvn: 'pending',
    card: 'info',
    device: 'default',
    ip: 'default',
    terminal: 'pending',
  }
  return (
    <Badge variant={map[type] ?? 'default'} className="uppercase">
      {type}
    </Badge>
  )
}

function SourceBadge({ source }: { source: BlockSource }) {
  const tone: React.ComponentProps<typeof Badge>['variant'] =
    source === 'court' ||
    source === 'efcc' ||
    source === 'nfiu' ||
    source === 'regulatory_order'
      ? 'failed'
      : source === 'automated'
        ? 'info'
        : source === 'alert' || source === 'case'
          ? 'pending'
          : 'default'
  return (
    <Badge variant={tone} className="capitalize">
      {source.replace(/_/g, ' ')}
    </Badge>
  )
}

function BlockStatusBadge({ status }: { status: BlockStatus }) {
  switch (status) {
    case 'active':
      return <Badge variant="failed">Active</Badge>
    case 'requested':
    case 'pending':
      return <Badge variant="pending">Awaiting approval</Badge>
    case 'approved':
      return <Badge variant="info">Approved</Badge>
    case 'lifted':
      return <Badge variant="success">Lifted</Badge>
    case 'rejected':
      return <Badge variant="cancelled">Rejected</Badge>
    case 'expired':
      return <Badge variant="default">Expired</Badge>
    default:
      return <Badge variant="default">{status}</Badge>
  }
}

// ---- helpers --------------------------------------------------------

function targetLabel(b: BlockSummary): string {
  // Prefer the backend-enriched display name (real customer name +
  // number, e.g. "Adekunle Ojo · DEMOC-LG-0042"). Falls back through
  // the legacy target.display_label, raw target_value, then the
  // numeric "Account #N" / "Customer #N" if nothing better is
  // available.
  const name = b.target_customer_display_name ?? b.linked_customer_display_name
  const number = b.target_customer_number ?? b.linked_customer_number
  if (name && number && number !== name) return `${name} · ${number}`
  if (name) return name
  if (b.target?.display_label) return b.target.display_label
  if (b.target_account_nuban) return b.target_account_nuban
  if (b.target_value) return b.target_value
  if (b.target_account_id) return `Account #${b.target_account_id}`
  if (b.target_customer_id) return `Customer #${b.target_customer_id}`
  return '—'
}

function deriveKpis(blocks: BlockSummary[]) {
  const out = {
    active: 0,
    requested: 0,
    approvedRecent: 0,
    rejectedRecent: 0,
    regulatory: 0,
  }
  const day = Date.now() - 24 * 3_600_000
  const week = Date.now() - 7 * 24 * 3_600_000
  for (const b of blocks) {
    if (b.status === 'active') out.active++
    if (b.status === 'requested' || b.status === 'pending') out.requested++
    if (b.status === 'approved') {
      const t = b.approved_at ? Date.parse(b.approved_at) : 0
      if (t >= day) out.approvedRecent++
    }
    if (b.status === 'rejected') {
      const t = b.rejected_at
        ? Date.parse(b.rejected_at)
        : b.created_at
          ? Date.parse(b.created_at)
          : 0
      if (t >= week) out.rejectedRecent++
    }
    if (
      b.source === 'court' ||
      b.source === 'efcc' ||
      b.source === 'nfiu' ||
      b.source === 'regulatory_order'
    ) {
      out.regulatory++
    }
  }
  return out
}

function periodCutoff(p: 'all' | 'h24' | 'd7' | 'd30'): number {
  if (p === 'h24') return Date.now() - 24 * 3_600_000
  if (p === 'd7') return Date.now() - 7 * 24 * 3_600_000
  if (p === 'd30') return Date.now() - 30 * 24 * 3_600_000
  return 0
}

function toggle<T extends string>(arr: T[], v: T): T[] {
  return arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]
}

function csv(s: string): string {
  if (s.includes(',') || s.includes('"') || s.includes('\n')) {
    return '"' + s.replace(/"/g, '""') + '"'
  }
  return s
}

function formatDay(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (!Number.isFinite(d.getTime())) return '—'
  return d.toLocaleDateString('en-NG', {
    month: 'short',
    day: 'numeric',
    year: '2-digit',
  })
}
